import {
  Typography,
  Stack,
  Dialog,
  Button,
  DialogTitle,
  DialogContent,
  Chip,
} from '@mui/material';
import {
  Datagrid,
  DateField,
  EditBase,
  ListContextProvider,
  ReferenceField,
  ResourceContextProvider,
  SaveButton,
  SimpleForm,
  TextField,
  TextFieldProps,
  TextInput,
  Toolbar,
  useGetMany,
  useGetManyReference,
  useList,
  useNotify,
  useRecordContext,
  useTranslate,
  useRefresh,
  useGetOne,
  useUpdate,
  warning,
  FunctionField,
} from 'react-admin';
import { QuickbooksPriceField } from '../components/QuickbooksPriceField';
import { PaymentNoteShow } from '../payments/PaymentNoteShow';
import { Contract } from '../types';
import { Divider, Box } from '@mui/material';
import PaymentReceiptButton from '../payments/PaymentReceiptButton';
import ResendReceiptButton from '../payments/ResendReceiptButton';
import LinkToQbButton from './LinkToQbButton';
import { useState } from 'react';
import EditIcon from '@mui/icons-material/Edit';
import { ClientSelectList } from '../payments/ClientSelectList';
import { EditInDialogButton } from '@react-admin/ra-form-layout';
import { useFormContext } from 'react-hook-form';

// Helper component to display processing status with color coding
const ProcessingStatusField = ({ source }: { source: string }) => (
  <FunctionField
    source={source}
    render={(record: any) => {
      const status = record[source];
      const colorMap: Record<string, 'success' | 'warning' | 'error' | 'default'> = {
        completed: 'success',
        processing: 'warning',
        failed: 'error',
        pending: 'default',
      };
      return (
        <Chip
          label={status || 'unknown'}
          color={colorMap[status] || 'default'}
          size="small"
        />
      );
    }}
  />
);

// Helper to format cents to dollars
const formatCentsToDollars = (cents: number | null | undefined): string => {
  if (cents === null || cents === undefined) return 'N/A';
  return `$${(cents / 100).toFixed(2)}`;
};

const ContractLinkedPaymentsList = () => {
  const translate = useTranslate();
  const record = useRecordContext<Contract>();

  const { data: supabasePayments } = useGetManyReference(
    'sb_payments',
    { target: 'contract_id', id: record?.id },
    { enabled: !!record }
  );

  const stripePayments = (supabasePayments || []).filter(
    (payment: any) => payment.payment_source !== 'partner'
  );
  const partnerPayments = (supabasePayments || []).filter(
    (payment: any) => payment.payment_source === 'partner'
  );

  const { data: qbPaymentsData, isLoading: isPaymentsLoading } = useGetMany(
    'payments',
    {
      ids: supabasePayments
        ? stripePayments.map((payment) => payment.accounting_reference_id).filter(Boolean)
        : [],
    },
    { enabled: stripePayments.length > 0 }
  );

  const payments = qbPaymentsData ? qbPaymentsData : [];

  const listContext = useList({ data: payments });
  const stripePaymentsListContext = useList({ data: stripePayments });
  const partnerPaymentsListContext = useList({ data: partnerPayments });
  
  if (isPaymentsLoading) return null;
  
  return (
    <>
      {/* Stripe Payments Section */}
      {stripePayments.length > 0 && (
        <Box my={4}>
          <Typography variant="h6">Stripe Payments ({stripePayments.length})</Typography>
          <Divider sx={{ mb: 2 }} />
          <ResourceContextProvider value="sb_payments">
            <ListContextProvider value={stripePaymentsListContext}>
              <Datagrid bulkActionButtons={false}>
                <TextField source="id" label="ID" />
                <FunctionField
                  label="Stripe"
                  render={(record: any) => {
                    const stripeId = record.stripe_payment_intent_id || record.stripe_session_id;
                    if (!stripeId) return 'N/A';
                    
                    // Determine if it's live based on the ID prefix
                    const isLive = stripeId.startsWith('cs_live_') || stripeId.startsWith('pi_live_');
                    const baseUrl = isLive 
                      ? 'https://dashboard.stripe.com/payments'
                      : 'https://dashboard.stripe.com/test/payments';
                    
                    const url = `${baseUrl}/${stripeId}`;
                    const label = record.stripe_payment_intent_id ? 'Payment Intent' : 'Checkout Session';
                    
                    return (
                      <a 
                        href={url}
                        target="_blank" 
                        rel="noopener noreferrer"
                        style={{ color: '#635BFF', textDecoration: 'none', fontWeight: 500 }}
                        title={`${label}: ${stripeId}`}
                      >
                        View in Stripe ↗
                      </a>
                    );
                  }}
                />
                <FunctionField
                  label="Amount"
                  render={(record: any) => formatCentsToDollars(record.amount_total)}
                />
                <FunctionField
                  label="Stripe Fee"
                  render={(record: any) => formatCentsToDollars(record.stripe_transaction_fee)}
                />
                <TextField source="payment_status" label="Payment Status" />
                <ProcessingStatusField source="processing_status" />
                <FunctionField
                  label="Created"
                  render={(record: any) => {
                    if (!record.created_at) return 'N/A';
                    try {
                      return new Date(record.created_at).toLocaleString();
                    } catch {
                      return 'Invalid date';
                    }
                  }}
                />
                <FunctionField
                  label="Processed"
                  render={(record: any) => {
                    if (!record.processed_at) return '-';
                    try {
                      return new Date(record.processed_at).toLocaleString();
                    } catch {
                      return 'Invalid date';
                    }
                  }}
                />
                <TextField source="quickbooks_sales_receipt_id" label="QB Receipt ID" />
                <TextField source="error_message" label="Error" />
                <ResendReceiptButton contractId={record?.id} variant="stripe" label="Receipt" />
              </Datagrid>
            </ListContextProvider>
          </ResourceContextProvider>
        </Box>
      )}

      {partnerPayments.length > 0 && (
        <Box my={4}>
          <Typography variant="h6">Partner Payments ({partnerPayments.length})</Typography>
          <Divider sx={{ mb: 2 }} />
          <ResourceContextProvider value="sb_payments">
            <ListContextProvider value={partnerPaymentsListContext}>
              <Datagrid bulkActionButtons={false}>
                <TextField source="id" label="ID" />
                <FunctionField
                  label="Amount"
                  render={(record: any) => formatCentsToDollars(record.amount_total)}
                />
                <TextField source="payment_status" label="Payment Status" />
                <ProcessingStatusField source="processing_status" />
                <TextField source="quickbooks_sales_receipt_id" label="QB Receipt ID" />
                <FunctionField
                  label="Processed"
                  render={(record: any) => {
                    if (!record.processed_at) return '-';
                    try {
                      return new Date(record.processed_at).toLocaleString();
                    } catch {
                      return 'Invalid date';
                    }
                  }}
                />
                <TextField source="error_message" label="Error" />
              </Datagrid>
            </ListContextProvider>
          </ResourceContextProvider>
        </Box>
      )}

      {/* QuickBooks Payments Section */}
      <Box my={4}>
        <Typography variant="h6">
          {translate('resources.payments.name', { smart_count: payments.length })}
        </Typography>
        <Divider />
        {payments.length == 0 ? (
          <Typography mt={1}>No payments</Typography>
        ) : (
          <ResourceContextProvider value="payments">
            <ListContextProvider value={listContext}>
              <Datagrid
                bulkActionButtons={false}
                // expand={<PaymentNoteShow />}
              >
                <TextField source="id" />
                <TextField source="client" />
                <DateField
                  source="MetaData.CreateTime"
                  label="resources.payments.fields.created_at"
                />
                <ReferenceField
                  source="PaymentMethodRef.value"
                  reference="paymentmethods"
                  label="resources.payments.fields.method"
                  link={false}
                >
                  <TextField source="Name" />
                </ReferenceField>
                <QuickbooksPriceField
                  source="TotalAmt"
                  label="resources.payments.fields.amount"
                />
                <TextField
                  source="PaymentRefNum"
                  label="resources.payments.fields.reference"
                />
                <DateField
                  source="TxnDate"
                  label="resources.payments.fields.payment_date"
                />

                <TextField
                  source="PrivateNote"
                  label="resources.payments.fields.note"
                />
                <PaymentReceiptButton source="id" funeralId={record?.funeral_id} />
                <ResendReceiptButton contractId={record?.id} variant="manual" label="Receipt" />
                <LinkToQbButton label="View in QB" type="payment" />
                {/* <EditPaymentButton label="Edit" /> */}
                <GetClientSelectList />
              </Datagrid>
            </ListContextProvider>
          </ResourceContextProvider>
        )}
      </Box>
    </>
  );
};

interface SavePaymentClientButtonProps {
  handleSubmitWithRedirect?: (redirect?: string) => void;
  // Include any other props that you might need
}

const SavePaymentClientButton: React.FC<SavePaymentClientButtonProps> = (
  props
) => {
  const [update] = useUpdate();
  const notify = useNotify();
  // const redirect = useRedirect();
  const form = useFormContext();

  const handleSave = async () => {
    const updatedRecord = form.getValues();
    // Assuming updatedRecord contains new client_id
    const newClientId = updatedRecord.client_id;

    try {
      await update('sb_payments', {
        id: updatedRecord.id,
        data: { client_id: newClientId },
        previousData: updatedRecord,
      });
      notify('Client updated successfully');
      // redirect('/payments');
    } catch (e: any) {
      notify(e.message, warning);
    }
  };

  return <SaveButton />;
};

const PaymentSaveToolbar = ({ paymentId, onClose }: { paymentId: string, onClose: () => void }) => {
  const [update] = useUpdate();
  const { getValues } = useFormContext();
  const notify = useNotify();
  const refresh = useRefresh();


  // const redirect = useRedirect();

  const handleClick = (e: any) => {
    e.preventDefault(); // necessary to prevent default SaveButton submit logic
    const { client_id } = getValues();
    console.log('', paymentId, client_id);

    update(
      'sb_payments',
      { id: paymentId, data: {client_id: client_id} },
    )
      .then(() => {
        notify('Payment Client Updated Successfully');
        refresh();
      })
      .catch((e) => {
        console.log(e);
      });
  };

  return (
    <Toolbar>
      <SaveButton type="button" onClick={handleClick} />
    </Toolbar>
  );
};

export const GetClientSelectList = () => {
  const record = useRecordContext<Contract>();
  const [isDialogOpen, setDialogOpen] = useState<boolean>(false);

  const handleClose = () => {
      setDialogOpen(false);
  };

  const { data: sbPayments, isLoading: isLoadingPayment } = useGetManyReference(
    'sb_payments',
    { target: 'accounting_reference_id', id: record?.id },
    { enabled: !!record }
  );

  const { data: contracts, isLoading } = useGetMany<Contract>(
    'contracts',
    { ids: sbPayments?.map((a) => a.contract_id) },
    { enabled: !isLoadingPayment }
  );

  
  if(!sbPayments) return null;

  return (
    <EditInDialogButton >
      <SimpleForm toolbar={<PaymentSaveToolbar paymentId={sbPayments![0].id} onClose={handleClose} />}>
        <ClientSelectList contract={contracts![0]} />
      </SimpleForm>
    </EditInDialogButton>
  );
};

export const EditPaymentButton = (props: TextFieldProps) => {
  const record = useRecordContext<Contract>();

  const { data: sbPayments, isLoading: isLoadingPayment } = useGetManyReference(
    'sb_payments',
    { target: 'accounting_reference_id', id: record?.id },
    { enabled: !!record }
  );

  const { data: contracts, isLoading } = useGetMany<Contract>(
    'contracts',
    { ids: sbPayments?.map((a) => a.contract_id) },
    { enabled: !isLoadingPayment }
  );

  const translate = useTranslate();
  const [editOpen, setEditOpen] = useState(false);

  const handleClickOpen = () => {
    setEditOpen(true);
  };

  const handleClose = () => {
    setEditOpen(false);
  };

  if (isLoading || isLoadingPayment || record == null) {
    return null;
  }

  return (
    <>
      <Button
        startIcon={<EditIcon />}
        onClick={handleClickOpen}
        aria-label={translate('resources.payments.buttonAdd')}
      ></Button>
      <Dialog open={editOpen} onClose={handleClose}>
        <DialogTitle>{translate('resources.payments.buttonEdit')}</DialogTitle>
        <DialogContent>
          <ResourceContextProvider value="sb_payments">
            <EditBase>
              <SimpleForm
                toolbar={
                  <Toolbar>
                    <DialogSaveButton setOpen={setEditOpen} />
                  </Toolbar>
                }
              >
                <ClientSelectList contract={contracts![0]} />
              </SimpleForm>
            </EditBase>
          </ResourceContextProvider>
        </DialogContent>
      </Dialog>
    </>
  );
};

const DialogSaveButton = ({
  setOpen,
}: {
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
}) => {
  const notify = useNotify();
  const refresh = useRefresh();
  return (
    <SaveButton
      mutationOptions={{
        onSuccess: () => {
          notify('resources.payments.updated');
          refresh();
          setOpen(false);
        },
      }}
      type="button"
    />
  );
};

export default ContractLinkedPaymentsList;
